import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import Link from "next/link";

import { cn } from "@/utils";

export function isInternalLink(href: string): boolean {
  // Relative paths are internal
  if (href?.startsWith("/")) {
    return true;
  }

  // Check if absolute URL matches binarly.io domain
  try {
    const url = new URL(href);
    return url.hostname === "binarly.io" || url.hostname === "www.binarly.io";
  } catch {
    // If URL parsing fails, treat as internal (could be a relative path)
    return true;
  }
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-secondary-700 bg-[url(/button-noise-bg.webp)] bg-blend-overlay text-primary-foreground glow glow-secondary hover:bg-secondary-700/70",
        outline:
          "border bg-white/15 shadow-xs hover:bg-white/20 border-gray-500",
        link: "text-primary underline-offset-4 hover:underline !rounded-none",
        "link-underline":
          "text-primary underline decoration-secondary-500 decoration-2 underline-offset-4 hover:underline",
        "link-glow":
          "relative text-primary no-underline border-b border-secondary-500 glow-border-b glow-secondary pb-0.5 transition-[color,box-shadow] !rounded-none",
        ghost: "bg-transparent hover:bg-transparent",
      },
      size: {
        default: "h-12 py-4 rounded-full gap-1.5 px-6 has-[>svg]:px-2.5",
        xs: "h-9 text-xs py-2 rounded-full gap-1.5 px-3 has-[>svg]:px-2.5",
        sm: "h-10 py-3 rounded-full gap-1.5 px-4 has-[>svg]:px-2.5",
        lg: "h-12 rounded-full px-8 has-[>svg]:px-6",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

/** Props invalid on `<a>` / `Link` when using `href` (button-only). */
function omitButtonOnlyProps<T extends Record<string, unknown>>(
  props: T
): Omit<
  T,
  | "form"
  | "formAction"
  | "formEncType"
  | "formMethod"
  | "formNoValidate"
  | "formTarget"
  | "name"
  | "value"
> {
  const {
    form: _f,
    formAction: _fa,
    formEncType: _fe,
    formMethod: _fm,
    formNoValidate: _fnv,
    formTarget: _ft,
    name: _n,
    value: _v,
    ...anchorProps
  } = props;
  return anchorProps;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  href,
  type = "button",
  disabled,
  children,
  ...rest
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    href?: string;
  }) {
  const classes = cn(
    buttonVariants({ variant, size, className }),
    !href &&
      !("onClick" in rest && rest.onClick) &&
      !asChild &&
      type !== "submit" &&
      "opacity-50"
  );

  if (href) {
    const isInternal = isInternalLink(href);
    const linkClasses = cn(
      classes,
      disabled && "pointer-events-none opacity-50"
    );
    const anchorCompatible = omitButtonOnlyProps({
      ...rest,
    } as Record<string, unknown>);
    const sharedProps = {
      "data-slot": "button" as const,
      className: linkClasses,
      "aria-disabled": disabled ? true : undefined,
      ...anchorCompatible,
    } as React.AnchorHTMLAttributes<HTMLAnchorElement> &
      React.PropsWithChildren<{ "data-slot"?: string; className?: string }>;

    if (isInternal) {
      return (
        <Link {...(sharedProps as React.ComponentProps<typeof Link>)} href={href}>
          {children}
        </Link>
      );
    }
    return (
      <a {...sharedProps} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      type={type}
      disabled={disabled}
      className={classes}
      {...rest}
    >
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
